/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './TransactionsModal.scss';

import { camelCase, map, startCase, noop } from 'lodash';
import { bool } from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { RasWithdrawModal } from '@trp-ii-ii-component-library/ii-components';

import ClickOffSensor from 'components/shared/ClickOffSensor/ClickOffSensor';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import {
    transactionsLinkBuilder,
    withdrawFromSweepAccount,
    openAnAccountUrl,
    transactions,
    projectCashFlow,
    balanceDetailsUrl
} from 'services/Externals';
import { openBrokerageAuthTab } from 'services/ExternalAuthorizationURLBuilder';
import Content from 'services/Content';
import { hasOwnedAllProductTypes } from 'state/view/ViewSelectors';
import {
  isActiveAdvisory,
  isMutualFund
} from 'services/Products';
import {
    balanceDetailsEnabled,
    rasWithdrawLinksEnabled
} from 'services/FeatureFlags';

export const LinkTextCreator = ({ name, label }) => {
  const content = Content.transactions;

  const labelCollegSavings = [40, 70, 80, content.label.collegeSavings];

  if (labelCollegSavings.includes(label) && name === content.name.exchange) {
    return (
      <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
        {content.collegeSavingsExchange}
      </div>
    );
  }

  if (name === content.name.tradeStocks) {
    return (
      <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
        {content.tradeStocks}
      </div>
    );
  }
  if (name === content.name.automaticTransactions) {
    return (
      <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
        {isMutualFund(label)
          ? content['automaticTransactionsMF']
          : content[camelCase(name)]}
      </div>
    );
  }
  return (
    <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
      {content[camelCase(name)] ? content[camelCase(name)] : ''}
    </div>
  );
};

const Header = ({ label }) => (
  <Fragment>
    <p className="transactions-modal-link-group-label">{label}</p>
    <div className="modal-divider" />
  </Fragment>
);

export const LinkGroup = ({
                            label,
                            links,
                            uberId = '',
                            productType,
                            includeHeader = true,
                            isMobile,
                            isRMDEligible,
                            isAccountDetails
                          }) => (
  <li
    className={`transactions-modal-link-group ${
      !label ? '' : `e2e-transactions-modal-link-group-${camelCase(label)}`
    }`}>
    {includeHeader && <Header label={label} />}
    <ul>
      {map(links, (link, name) => (
        <li key={name} className="transactions-modal-link">
          {/* Temporary modal in place of link to full RAS withdraw */}
          {/* TODO: remove modal and isMobile props before full RAS withdraw rollout */}
          {isActiveAdvisory(productType) &&
          name === 'withdraw' &&
          !rasWithdrawLinksEnabled() ? (
            <RasWithdrawModal isMobile={isMobile} />
          ) : (
            <a
              href={
                !uberId
                  ? link
                  : link && link.indexOf('?') === -1
                    ? `${link}?uid=${uberId}`
                    : `${link}&uid=${uberId}` // Append UBER ID for CWV if it exists
              }>
              {name === 'rollOver' ? (
                <span>{'Roll Over & Transfer'}</span>
              ) : name === 'automaticTransactions' && isAccountDetails ? (
                <span>{'Set Up Automatic Transactions'}</span>
              ) : name === 'rmd' && isRMDEligible ? (
                <span>{'Manage My RMD'}</span>
              ): (
                startCase(name)
              )}
            </a>
          )}
          <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
            <LinkTextCreator label={label} name={name} />
          </div>
        </li>
      ))}
    </ul>
  </li>
);

export const brokerageActions = {
  trade: {
    'Trade Stocks & ETFs': 'TRADING',
    'Trade Mutual Funds': 'MF_TRADING',
    'Trade Options': 'OPT_TRADING',
    'Withdraw from Sweep Account': 'SWEEP'
  },
  view: {
    'Order Status': 'ORDERSTATUS',
    'Balance details': 'BALANCEDETAILS',
    'Projected Cash Flow': 'PCF'
  }
};

export const linkDestination = (type, isPAM, accountNumber, sweepAccountId, accountId) => {
  if (type === 'SWEEP') {
    return sweepAccountId
      ? `${withdrawFromSweepAccount}&selectedAgreementIDs=${sweepAccountId}`
      : withdrawFromSweepAccount;
  } else if (type === 'PCF') {
    return accountNumber
      ? `${projectCashFlow}?id=${accountNumber}`
      : projectCashFlow;
  } else if (type === 'BALANCEDETAILS') {
    return accountNumber
      ? `${balanceDetailsUrl}?id=${accountId}`
      : balanceDetailsUrl;
  }
  return noop;
};

export const brokerageClickHandler = (accountId, type) => {
  return type === 'SWEEP' || type === 'BALANCEDETAILS' || type === 'PCF' ? null : openBrokerageAuthTab(accountId, type);
};

export const BrokerageActionLink = ({
                               name,
                               type,
                               accountId,
                               accountNumber,
                               isPAM,
                               className = '',
                               sweepAccountId
                             }) => {
  return (
    <li className={`transactions-modal-link ${className}`}>
      <a
        href={linkDestination(type, isPAM, accountNumber, sweepAccountId, accountId)}
        rel="noreferrer"
        target={type === 'SWEEP' || type === 'BALANCEDETAILS' || type === 'PCF' ? '_self' : '_blank'}
        id={`brokerage-action-link-${name}-${accountId}`}
        onClick={() => brokerageClickHandler(accountId, type)}
        onKeyDown={(e) => {
          if(e.key === 'Enter') {
            const link = document.getElementById(`brokerage-action-link-${name}-${accountId}`);
            if(link) {
              link.click();
            }
          }
        }}
        tabIndex="0">
        {name}
      </a>
      <div className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
        <LinkTextCreator name={name} label="Brokerage" />
      </div>
    </li>
  );
};

const BrokerageActionLinkWithPermissions =
  WithUserPermissions(BrokerageActionLink);

const BrokerageActionLinks = ({
                                brokerageActions,
                                accountId,
                                accountNumber,
                                accounts,
                                isPAM,
                                includeHeader = true,
                                sweepAccountId,
                              }) => {
  const styleOverride = {
    display: 'block',
    width: '200px',
    wordBreak: 'break-word'
  };
  return (
    <li className="transactions-modal-link-group e2e-transactions-modal-link-group-brokerage">
      {includeHeader && <Header label="Brokerage" />}
      <ul>
        {map(brokerageActions.trade, (type, name) => (
          <BrokerageActionLinkWithPermissions
            name={name}
            accountId={accountId}
            accountNumber={accountNumber}
            accounts={accounts}
            isPAM={isPAM}
            sweepAccountId={sweepAccountId}
            type={type}
            shouldDisable={type !== 'SWEEP'}
            styleOverride={styleOverride}
          />
        ))}
        {map(brokerageActions.view, (type, name) => {
          if (name === 'Balance details' && !balanceDetailsEnabled()) {
            return null;
          }
          const monitorStyles = classnames('transactions-modal-link-monitor', {
            'transactions-modal-link-monitor-orderstatus':
              name === 'Order Status' || name === 'Balance Details' || name === 'Projected Cash Flow'
          });
          return (
            <BrokerageActionLinkWithPermissions
              name={name}
              accountId={accountId}
              accountNumber={accountNumber}
              accounts={accounts}
              isPAM={isPAM}
              sweepAccountId={sweepAccountId}
              type={type}
              className={monitorStyles}
              shouldDisable={type !== 'PCF'}
              styleOverride={styleOverride}
            />
          );
        })}
      </ul>
    </li>
  );
};

export const BrokerageActionLinksWithPermissions =
  WithUserPermissions(BrokerageActionLinks);

class TransactionsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false
    };

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  open() {
    this.setState({ show: true });
  }

  close() {
    this.setState({ show: false });
  }

  render() {
    const { hasOwnedAllProducts, accounts, view, uberId } = this.props;
    const {
      hasOwnedActivePlus,
      hasOwnedCollegeSavings,
      hasOwnedMutualFunds,
      isBrokerageOwner,
      hasRelatedBrokerage,
      hasRelatedActivePlus,
      hasRelatedCollegeSavings,
      hasRelatedMutualFund,
      isPAM
    } = view;
    const { show } = this.state;

    const updateFlexDirection = classnames({
      row: hasOwnedAllProducts
    });

    const additionalLinksClassName = classnames({
      'col-6': hasOwnedAllProducts
    });

    return (
      <ClickOffSensor
        className="transactions-modal"
        isOpen={show}
        onClickOut={this.close}>
        <button
          className={
            'transactions-modal-toggle-modal-button ' + (show ? 'open' : '')
          }
          id="start-a-transaction-button-id"
          onClick={show ? this.close : this.open}>
          Start a Transaction{' '}
          <span
            className={
              'chevron ' + (show ? 'aa-chevron-up' : 'aa-chevron-down')
            }
          />
        </button>
        {show && (
          <div className="transactions-modal-links-container">
            <ul className="transactions-modal-drop-down-content">
              {(hasOwnedMutualFunds || hasRelatedMutualFund) && (
                <LinkGroup
                  label="Mutual Funds"
                  links={transactionsLinkBuilder('mutualFund', isPAM)}
                />
              )}
              {
                (hasOwnedActivePlus || hasRelatedActivePlus) && (
                  <LinkGroup
                    label=" ActivePlus Portfolios"
                    links={transactionsLinkBuilder(
                      'activePlusOtherTransactions',
                      isPAM
                    )}
                    uberId={uberId}
                  />
                ) // Include UBER ID for Customer Web View
              }
              {(hasOwnedCollegeSavings || hasRelatedCollegeSavings) && (
                <LinkGroup
                  label="529 College Savings Plans"
                  links={transactionsLinkBuilder('collegeSavings', isPAM)}
                />
              )}
              {(isBrokerageOwner || hasRelatedBrokerage) && (
                <BrokerageActionLinksWithPermissions
                  brokerageActions={brokerageActions}
                  shouldDisable={false}
                  accounts={accounts}
                  isPAM={isPAM}
                />
              )}
            </ul>
            <div
              className={`transactions-modal-additional-links ${updateFlexDirection}`}>
              <span className={additionalLinksClassName}>
                <a href={openAnAccountUrl(view)}>Open a New Account</a>
                <p className="transactions-modal-additional-link-subtext transactions-modal-additional-link-spacing">
                  Start a new account and fund it electronically or by check.
                </p>
              </span>
              <span className={additionalLinksClassName}>
                <a
                  href={
                    isPAM
                      ? transactions.otherLinks.pamSeeAllTransactions
                      : transactions.otherLinks.seeAllTransactions
                  }>
                  See All Transactions
                </a>
                <p className="transactions-modal-additional-link-subtext">
                  Looking to roll over or complete other transactions?
                </p>
              </span>
            </div>
          </div>
        )}
      </ClickOffSensor>
    );
  }
}

TransactionsModal.propTypes = {
  hasOwnedActivePlus: bool,
  hasOwnedCollegeSavings: bool,
  hasOwnedMutualFunds: bool,
  isBrokerageOwner: bool,
  hasRelatedBrokerage: bool,
  hasRelatedMutualFund: bool,
  hasRelatedActivePlus: bool,
  hasRelatedCollegeSavings: bool
};

const mapStateToProps = (state) => ({
  view: state.view,
  accounts: state.accounts,
  hasOwnedAllProducts: hasOwnedAllProductTypes(state),
  uberId: state.customer.webViewEnabled ? state.customer.uberId : ''
});

export default connect(mapStateToProps)(TransactionsModal);
